import {
  type MsalGuardConfiguration,
  type MsalInterceptorConfiguration,
  MSAL_GUARD_CONFIG,
  MSAL_INSTANCE,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';
import {
  BrowserCacheLocation,
  InteractionType,
  type IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';

import type { AuthConfig } from '../../../environments/auth-config.model';

function resolveRedirectUri(auth: AuthConfig): string {
  if (auth.redirectUri) {
    return auth.redirectUri;
  }

  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/`;
  }

  return '/';
}

export function createMsalInstance(auth: AuthConfig): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: auth.clientId,
      authority: `https://login.microsoftonline.com/${auth.tenantId}`,
      redirectUri: resolveRedirectUri(auth),
      postLogoutRedirectUri: auth.postLogoutRedirectUri ?? resolveRedirectUri(auth),
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Warning,
      },
    },
  });
}

export function createMsalGuardConfig(auth: AuthConfig): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: auth.apiScopes,
    },
  };
}

export function createMsalInterceptorConfig(auth: AuthConfig): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, string[] | null>();
  protectedResourceMap.set('/api', auth.apiScopes);
  protectedResourceMap.set('/api/*', auth.apiScopes);

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function provideMsalAuth(auth: AuthConfig) {
  return [
    {
      provide: MSAL_INSTANCE,
      useFactory: () => createMsalInstance(auth),
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: () => createMsalGuardConfig(auth),
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: () => createMsalInterceptorConfig(auth),
    },
  ];
}
